import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import PrimaryBlueButton from '../../common/buttons/PrimaryBlueButton';
import { colors } from '../../../styles/fontStyles';
import { useThemeContext } from '../../../contexts/BaseThemeContextProvider';
import { TestKitActivationLastSteps } from '../../../types/user';
import HowToCollectCarouselWithVideos from '../../pages/howTo/HowToCollectCarouselWithVideos';

interface SuccessfulRegistrationProps {
  finalStepsData: TestKitActivationLastSteps;
  isGuest: boolean;
}

export default function SuccessfulActivationOrRegistration({ finalStepsData, isGuest }: SuccessfulRegistrationProps) {
  const navigate = useNavigate();
  const { theme } = useThemeContext();

  const { shopifyOrderId, activationCode } = finalStepsData;

  const onNavigateToHowToCollectSample = () => {
    navigate('/how-to-collect-sample.html', {
      state: {
        shopifyOrderId,
      },
    });
  };

  const renderHowToCollectSampleButton = () => {
    return (
      <PrimaryBlueButton
        isLoading={false}
        onClick={onNavigateToHowToCollectSample}
        buttonText="How to collect a good sample"
        sxProps={{
          padding: '10px 20px',
          marginRight: '13px',
          width: { xs: '100%', sm: 'auto' },
          marginBottom: { xs: '10px', sm: '0' },
        }}
      />
    );
  };

  return (
    <Box sx={{ marginBottom: '100px' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          [theme.breakpoints.up('md')]: {
            flexDirection: 'row',
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            [theme.breakpoints.up('md')]: {
              width: '100%',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              margin: 'auto',
              pd: 2,
              flexDirection: 'column',
              maxWidth: 600,
            }}
          >
            <Typography variant="h5" sx={{ mb: 1 }}>
              Test kit order
            </Typography>
            <Typography
              variant="h5"
              sx={{ color: colors.white, mb: 1, background: colors.primaryBlue, width: 'fit-content' }}
            >
              {activationCode}
            </Typography>
            <Typography variant="h4" sx={{ mb: 1, typography: 'h4' }}>
              {isGuest
                ? 'Your registration and activation were successfully completed'
                : 'Your activation was successfully completed'}
            </Typography>
            <Typography variant="h5" sx={{ mb: 1, typography: 'h5' }}>
              IMPORTANT
            </Typography>
            <Typography
              variant="body2"
              sx={{
                marginBottom: '14px',
                fontSize: '15px',
                color:
                  theme.custom.components.guestTestKitActivation.steps.successfulActivationOrRegistration
                    .explanationText.color,
              }}
            >
              Once you have collected your sample,
              close the purple container and attach the sample label sticker to the front.
              Please make sure you complete all the information on the sample label sticker or else we will have to
              contact you before releasing your results. See this video for more.
            </Typography>
            <Box
              component="img"
              src={'/images/how-to-collect-sample/label-your-sample.png'}
              alt="Home"
              sx={{
                alignItems: 'center',
                width: '380px',
                height: '180px',
                borderRadius: '16px',
                backgroundColor: 'white',
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              margin: 'auto',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: { xs: 'center', sm: 'space-between' },
              maxWidth: { xs: '100%', sm: '600px' },
              paddingTop: '10px',
            }}
          >
            {renderHowToCollectSampleButton()}
          </Box>
        </Box>
        <Box
          sx={{
            width: '100%',
            margin: { xs: '34px auto' },
            [theme.breakpoints.up('sm')]: {
              width: '85%',
              margin: '26px auto',
            },
            [theme.breakpoints.up('md')]: {
              width: '60%',
              margin: '11px 29px 0 29px',
            },
          }}
        >
          <HowToCollectCarouselWithVideos />
        </Box>
      </Box>
    </Box>
  );
}
